// Vendors
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Actions
import { AUTH_STEPS, MFA_OPTIONS, updateAuthState } from '../reducers/auth/authActions';

// Selectors
import { 
  selectStep, 
  selectSelectedMFA, 
  selectSignInServerError,
  selectAppUrl,
} from '../reducers/auth/authSelectors';

// Helpers
import { clearAuthSession } from '../reducers/auth/authAuthorizeHelpers';

// Components
import Stepper from '../components/Stepper/Stepper';
import AuthCardLayout from '../components/AuthCardLayout/AuthCardLayout';
import SignInForm from './NoSession/SignInForm/SignInForm';
import ChooseMFA from './NoSession/ChooseMFA/ChooseMFA';
import EnterOTP from './NoSession/EnterOTP/EnterOTP';
import MessageBanner from '../components/MessageBanner/MessageBanner';

const steps = [
  { id: AUTH_STEPS.SIGN_IN, component: SignInForm, route: '/sign-in' },
  { id: AUTH_STEPS.CHOOSE_MFA, component: ChooseMFA, route: '/choose-mfa' },
  { id: AUTH_STEPS.ENTER_OTP, component: EnterOTP, route: '/enter-otp' },
];

const AuthPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');
  const activeStep = useSelector(selectStep);
  const selectedMFA = useSelector(selectSelectedMFA);
  const signInError = useSelector(selectSignInServerError);
  const appUrl = useSelector(selectAppUrl);

  // Sync URL with step
  useEffect(() => {
    const currentStep = steps.find(s => s.id === activeStep);
    if (currentStep?.route && location.pathname !== currentStep.route) {
      navigate(currentStep.route, { replace: true });
    }
  }, [activeStep, location.pathname, navigate]);

  const layoutClassName = activeStep === AUTH_STEPS.ENTER_OTP
    ? (selectedMFA === MFA_OPTIONS.EMAIL ? 'layout-enter-otp-email' : 'layout-enter-otp-sms')
    : '';

  const isResendLimitTopBanner = activeStep === AUTH_STEPS.SIGN_IN && signInError === 'otpResendLimitSignInBannerError';

  const handleCloseBanner = () => {
    dispatch(updateAuthState({ error: null }));
    if (appUrl) {
      clearAuthSession();
      window.location.href = appUrl;
    }
  };

  return (
    <>
      {isResendLimitTopBanner && (
        <MessageBanner
          content={t(signInError)}
          onClose={handleCloseBanner}
        />
      )}
      <AuthCardLayout 
        className={layoutClassName}
        showBookmarkWarning={activeStep === AUTH_STEPS.SIGN_IN}
      >
        <Stepper steps={steps} activeStep={activeStep} />
      </AuthCardLayout>
    </>
  );
};

export default AuthPage;